import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { FileText, Building2, Users, Wrench, AlertTriangle, Shield, CheckCircle, ChevronLeft, ChevronRight, Plus, Trash2, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS = [
  { key: 'general', label: 'Datos Generales', icon: FileText },
  { key: 'organizacion', label: 'Organizacion', icon: Building2 },
  { key: 'personal', label: 'Personal', icon: Users },
  { key: 'recursos', label: 'Recursos', icon: Wrench },
  { key: 'riesgos', label: 'Riesgos', icon: AlertTriangle },
  { key: 'sso', label: 'Preguntas SSO', icon: Shield },
  { key: 'validacion', label: 'Validacion', icon: CheckCircle },
];

const DEFAULT_SSO = [
  "Se verifico que todo el personal cuenta con la capacitacion y competencias requeridas para la tarea",
  "Se realizo la charla de seguridad previa al inicio de la actividad",
  "Se verifico el estado de los equipos y herramientas a utilizar",
  "Se identificaron y evaluaron los riesgos asociados a la tarea",
  "Se verifico que existen los permisos de trabajo necesarios",
  "Se cuenta con los elementos de proteccion personal requeridos",
  "Se verificaron las condiciones climaticas y del entorno de trabajo",
  "Se establecieron las vias de evacuacion y puntos de encuentro",
];

const RIESGO_COLORS = { BAJO: 'bg-green-500', MEDIO: 'bg-yellow-500', ALTO: 'bg-orange-500', CRITICO: 'bg-red-500' };
const ESTADO_REC = { Operativo: 'bg-emerald-100 text-emerald-700', 'En Mantencion': 'bg-amber-100 text-amber-700', 'Fuera de Servicio': 'bg-red-100 text-red-700' };

function EmpresaSearchSelect({ empresas, onSelect }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.length >= 1
    ? empresas.filter(e => e.razon_social.toLowerCase().includes(query.toLowerCase()))
    : [];

  return (
    <div className="relative" ref={ref} data-testid="empresa-search-select">
      <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400 z-10 pointer-events-none" />
      <input type="text" value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={`Buscar entre ${empresas.length} empresas contratistas...`}
        className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a05e49]/20"
        data-testid="input-empresa-search" />
      {open && query.length >= 1 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-[220px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-slate-400 text-center">No se encontraron empresas</div>
          ) : (
            <>
              {filtered.map(emp => (
                <button key={emp.id} type="button"
                  onMouseDown={e => { e.preventDefault(); onSelect(emp); setQuery(''); setOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#a05e49]/5 transition-colors border-b border-slate-50 last:border-0"
                  data-testid={`emp-option-${emp.id}`}>
                  {emp.razon_social}
                </button>
              ))}
              {filtered.length > 0 && <div className="px-3 py-1.5 text-[10px] text-slate-400 text-center border-t">{filtered.length} resultado{filtered.length !== 1 ? 's' : ''}</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RecursosStep({ catalogoRecursos, selected, onAdd, onRemove }) {
  const [search, setSearch] = useState('');
  const [fTipo, setFTipo] = useState('');
  const [fEmpresa, setFEmpresa] = useState('');

  const tipos = [...new Set(catalogoRecursos.map(r => r.tipo))].sort();
  const empresas = [...new Set(catalogoRecursos.map(r => r.empresa_nombre).filter(Boolean))].sort();

  const available = catalogoRecursos.filter(r => {
    if (selected.some(s => s.codigo === r.codigo)) return false;
    if (fTipo && r.tipo !== fTipo) return false;
    if (fEmpresa && r.empresa_nombre !== fEmpresa) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.codigo.toLowerCase().includes(q) || r.nombre.toLowerCase().includes(q) || r.marca.toLowerCase().includes(q) || r.patente.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2"><Wrench className="w-5 h-5 text-slate-500" /><h2 className="text-lg font-bold text-slate-800">Recursos Asignados</h2>
        <span className="text-xs bg-[#a05e49]/10 text-[#a05e49] px-2 py-0.5 rounded-full font-medium">{selected.length} seleccionado(s)</span>
      </div>

      {/* Selected resources */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 pb-3 border-b border-slate-200">
          {selected.map((r, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#a05e49]/10 text-[#a05e49] text-xs font-medium rounded-full border border-[#a05e49]/20">
              <span className="font-mono">{r.codigo}</span> {r.nombre}
              <button onClick={() => onRemove(i)} className="hover:text-red-600 ml-0.5">&times;</button>
            </span>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por codigo, nombre, marca o patente..."
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a05e49]/20" data-testid="filter-recurso-search" />
        </div>
        <select value={fTipo} onChange={e => setFTipo(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-recurso-tipo">
          <option value="">Todos los tipos</option>
          {tipos.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={fEmpresa} onChange={e => setFEmpresa(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-recurso-empresa">
          <option value="">Todas las empresas</option>
          {empresas.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
      </div>

      {/* Available catalog table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[340px] overflow-y-auto">
        <table className="w-full" data-testid="catalogo-table">
          <thead className="sticky top-0 bg-slate-50 z-10">
            <tr className="border-b border-slate-200">
              <th className="w-10 px-3 py-2"></th>
              <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase">Codigo</th>
              <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase">Nombre</th>
              <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase">Tipo</th>
              <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase">Empresa</th>
              <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase">Marca / Modelo</th>
              <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase">Patente</th>
              <th className="text-center px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {available.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-slate-400">
                {catalogoRecursos.length === 0 ? 'No hay recursos en el catalogo. Agregue recursos desde el mantenedor.' : 'No se encontraron recursos con los filtros aplicados.'}
              </td></tr>
            ) : available.map(r => (
              <tr key={r.id} className="hover:bg-[#a05e49]/5 cursor-pointer transition-colors" onClick={() => onAdd(r)} data-testid={`cat-row-${r.id}`}>
                <td className="px-3 py-2 text-center"><Plus className="w-4 h-4 text-[#a05e49] mx-auto" /></td>
                <td className="px-3 py-2 text-xs font-mono text-slate-600">{r.codigo}</td>
                <td className="px-3 py-2 text-xs text-slate-800 font-medium">{r.nombre}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.tipo}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.empresa_nombre}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.marca}{r.modelo ? ` / ${r.modelo}` : ''}</td>
                <td className="px-3 py-2 text-xs font-mono text-slate-500">{r.patente}</td>
                <td className="px-3 py-2 text-center"><span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${ESTADO_REC[r.estado] || 'bg-slate-100 text-slate-500'}`}>{r.estado}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-slate-400">{available.length} recurso(s) disponible(s) en el catalogo. Click en una fila para agregar.</p>
    </div>
  );
}

function PersonalStep({ token, mandanteEjecuta, empresasIds, empresas, selected, onAdd, onRemove }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fOrigen, setFOrigen] = useState('');
  const [fEmpresaId, setFEmpresaId] = useState('');
  const [disponibles, setDisponibles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Debounce search input (400ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const needsFilter = fOrigen || (debouncedSearch.length >= 2);
    if (!needsFilter) { setDisponibles([]); return; }
    const fetchPersonal = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (fOrigen) params.set('origen', fOrigen);
        if (fEmpresaId) params.set('empresa_id', fEmpresaId);
        if (debouncedSearch.length >= 2) params.set('search', debouncedSearch);
        const res = await axios.get(`${API}/personal-disponible?${params}`, { headers });
        setDisponibles(res.data);
      } catch { }
      setLoading(false);
    };
    fetchPersonal();
  }, [token, fOrigen, fEmpresaId, debouncedSearch]);

  const available = disponibles.filter(p => !selected.some(s => s.rut === p.rut));

  // Empresas contratistas de mandante 2
  const empresasMandante2 = empresas.filter(e => e.gerencia_nombre);
  const empresasContratistas = empresasMandante2.filter(e => empresasIds.includes(e.id));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2"><Users className="w-5 h-5 text-slate-500" /><h2 className="text-lg font-bold text-slate-800">Personal Asignado</h2>
        <span className="text-xs bg-[#a05e49]/10 text-[#a05e49] px-2 py-0.5 rounded-full font-medium">{selected.length} asignado(s)</span>
      </div>

      {/* Selected personnel as chips grouped */}
      {selected.length > 0 && (
        <div className="pb-3 border-b border-slate-200 space-y-2">
          {selected.filter(s => s.empresa === 'Lomas Bayas').length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Lomas Bayas</p>
              <div className="flex flex-wrap gap-1.5">
                {selected.filter(s => s.empresa === 'Lomas Bayas').map((p, i) => {
                  const realIdx = selected.indexOf(p);
                  return (
                    <span key={realIdx} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#1B919B]/10 text-[#1B919B] text-xs font-medium rounded-full border border-[#1B919B]/20">
                      {p.nombre} <button onClick={() => onRemove(realIdx)} className="hover:text-red-600">&times;</button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {selected.filter(s => s.empresa !== 'Lomas Bayas').length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Contratistas</p>
              <div className="flex flex-wrap gap-1.5">
                {selected.filter(s => s.empresa !== 'Lomas Bayas').map((p, i) => {
                  const realIdx = selected.indexOf(p);
                  return (
                    <span key={realIdx} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#a05e49]/10 text-[#a05e49] text-xs font-medium rounded-full border border-[#a05e49]/20">
                      {p.nombre} <span className="text-[9px] text-slate-400">({p.empresa})</span> <button onClick={() => onRemove(realIdx)} className="hover:text-red-600">&times;</button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre o RUT (min 2 caracteres)..."
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a05e49]/20" data-testid="filter-personal-search" />
        </div>
        <select value={fOrigen} onChange={e => { setFOrigen(e.target.value); setFEmpresaId(''); }}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-personal-origen">
          <option value="">Seleccione Empresa</option>
          <option value="mandante">Lomas Bayas (Mandante)</option>
          <option value="contratista">Contratistas</option>
        </select>
        {fOrigen === 'contratista' && (
          <select value={fEmpresaId} onChange={e => setFEmpresaId(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-personal-empresa">
            <option value="">Todas las contratistas</option>
            {empresasContratistas.length > 0
              ? empresasContratistas.map(e => <option key={e.id} value={e.id}>{e.razon_social}</option>)
              : empresasMandante2.map(e => <option key={e.id} value={e.id}>{e.razon_social}</option>)
            }
          </select>
        )}
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[340px] overflow-y-auto">
        <table className="w-full" data-testid="personal-catalogo-table">
          <thead className="sticky top-0 bg-slate-50 z-10">
            <tr className="border-b border-slate-200">
              <th className="w-10 px-3 py-2"></th>
              <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase">RUT</th>
              <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase">Nombre</th>
              <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase">Cargo</th>
              <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase">Empresa</th>
              <th className="text-center px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase">Origen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1B919B] mx-auto" /></td></tr>
            ) : available.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-slate-400">
                {!fOrigen && debouncedSearch.length < 2 ? 'Seleccione una empresa o ingrese al menos 2 caracteres para buscar' : 'No se encontro personal con los filtros aplicados'}
              </td></tr>
            ) : available.map(p => (
              <tr key={p.id + p.origen} className="hover:bg-[#a05e49]/5 cursor-pointer transition-colors"
                onClick={() => onAdd({ rut: p.rut, nombre: p.nombre, rol: p.cargo, empresa: p.empresa })} data-testid={`per-row-${p.id}`}>
                <td className="px-3 py-2 text-center"><Plus className="w-4 h-4 text-[#a05e49] mx-auto" /></td>
                <td className="px-3 py-2 text-xs font-mono text-slate-600 whitespace-nowrap">{p.rut}</td>
                <td className="px-3 py-2 text-xs text-slate-800 font-medium">{p.nombre}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{p.cargo}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{p.empresa}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${p.origen === 'mandante' ? 'bg-[#1B919B]/10 text-[#1B919B]' : 'bg-[#a05e49]/10 text-[#a05e49]'}`}>
                    {p.origen === 'mandante' ? 'Mandante' : 'Contratista'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-slate-400">{available.length} persona(s) disponible(s). Click en una fila para asignar.</p>
    </div>
  );
}

const RIESGO_NIVEL_COLORS = { BAJO: 'bg-green-500', MEDIO: 'bg-yellow-500', ALTO: 'bg-orange-500', CRITICO: 'bg-red-500' };
const CONTROL_TIPOS = ['EPP', 'Ingenieria', 'Administrativo', 'Capacitacion', 'Senalizacion', 'Eliminacion', 'Sustitucion'];

function RiesgosStep({ token, catalogoRiesgos, areaId, sectorId, areaNombre, sectorNombre, riesgos, onUpdate }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [autoLoaded, setAutoLoaded] = useState(false);
  const [riesgoInput, setRiesgoInput] = useState('');
  const [controlInput, setControlInput] = useState('');
  const [controlTipo, setControlTipo] = useState('EPP');
  const [nivel, setNivel] = useState('MEDIO');
  const [showRiesgoSugg, setShowRiesgoSugg] = useState(false);
  const [showControlSugg, setShowControlSugg] = useState(false);
  const riesgoRef = useRef(null);
  const controlRef = useRef(null);

  // Auto-load risks from catalog for the selected area/sector
  useEffect(() => {
    if (autoLoaded || !areaId || catalogoRiesgos.length === 0) return;
    const matching = catalogoRiesgos.filter(cr => {
      if (cr.area_id !== areaId) return false;
      if (sectorId && cr.sector_id && cr.sector_id !== sectorId) return false;
      return !cr.sector_id || cr.sector_id === sectorId;
    });
    if (matching.length > 0 && riesgos.length === 0) {
      const autoRiesgos = matching.map(cr => ({
        descripcion: cr.nombre, nivel: cr.nivel || 'MEDIO', probabilidad: 3, severidad: 2,
        medida_control: cr.controles.map(c => `${c.nombre} (${c.tipo})`).join(', ')
      }));
      onUpdate(autoRiesgos);
    }
    setAutoLoaded(true);
  }, [areaId, sectorId, catalogoRiesgos, autoLoaded]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (riesgoRef.current && !riesgoRef.current.contains(e.target)) setShowRiesgoSugg(false);
      if (controlRef.current && !controlRef.current.contains(e.target)) setShowControlSugg(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Filtered catalog for autocomplete
  const riesgoSuggestions = riesgoInput.length >= 2
    ? catalogoRiesgos.filter(cr => cr.nombre.toLowerCase().includes(riesgoInput.toLowerCase()) && !riesgos.some(r => r.descripcion === cr.nombre)).slice(0, 8)
    : [];

  // All controls from catalog for autocomplete
  const allControles = catalogoRiesgos.flatMap(cr => cr.controles.map(c => ({ ...c, riesgoNombre: cr.nombre })));
  const controlSuggestions = controlInput.length >= 2
    ? allControles.filter(c => c.nombre.toLowerCase().includes(controlInput.toLowerCase())).slice(0, 8)
    : [];

  // Available catalog risks not yet added
  const availableCatalog = catalogoRiesgos.filter(cr => {
    if (cr.area_id !== areaId) return false;
    if (sectorId && cr.sector_id && cr.sector_id !== sectorId) return false;
    return !riesgos.some(r => r.descripcion === cr.nombre);
  });

  // Other catalog risks (different area)
  const otherCatalog = catalogoRiesgos.filter(cr => {
    if (cr.area_id === areaId) return false;
    return !riesgos.some(r => r.descripcion === cr.nombre);
  });

  const addFromCatalog = (cr) => {
    const controles = cr.controles.map(c => `${c.nombre} (${c.tipo})`).join(', ');
    onUpdate([...riesgos, { descripcion: cr.nombre, nivel: cr.nivel || 'MEDIO', probabilidad: 3, severidad: 2, medida_control: controles }]);
  };

  const addManual = () => {
    if (!riesgoInput.trim()) return;
    const controlStr = controlInput ? `${controlInput} (${controlTipo})` : '';
    onUpdate([...riesgos, { descripcion: riesgoInput.trim(), nivel, probabilidad: 3, severidad: 2, medida_control: controlStr }]);
    // Save to catalog backend
    if (areaId) {
      axios.post(`${API}/riesgos`, {
        nombre: riesgoInput.trim(), descripcion: riesgoInput.trim(),
        area_id: areaId, sector_id: sectorId || null, nivel
      }, { headers }).then(() => {
        if (controlInput.trim()) {
          // We don't have the riesgo ID yet from response, this is a best-effort save
        }
      }).catch(() => {});
    }
    setRiesgoInput(''); setControlInput(''); setControlTipo('EPP'); setNivel('MEDIO');
  };

  const removeRiesgo = (i) => onUpdate(riesgos.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-5 h-5 text-slate-500" />
        <h2 className="text-lg font-bold text-slate-800">Riesgos Identificados</h2>
        <span className="text-xs bg-[#a05e49]/10 text-[#a05e49] px-2 py-0.5 rounded-full font-medium">{riesgos.length} asignado(s)</span>
      </div>

      {/* Auto-loaded notice */}
      {areaId && autoLoaded && riesgos.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-[#1B919B]/5 rounded-lg border border-[#1B919B]/20">
          <span className="text-xs text-[#1B919B]">Se cargaron automaticamente los riesgos del area <b>{areaNombre}</b>{sectorNombre ? <> / sector <b>{sectorNombre}</b></> : ''}. Puede agregar o quitar segun corresponda.</span>
        </div>
      )}

      {/* Available from catalog for this area */}
      {availableCatalog.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-semibold text-slate-400 uppercase">Disponibles del area {areaNombre}</p>
          <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[180px] overflow-y-auto">
            <table className="w-full" data-testid="catalogo-riesgos-table">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="border-b border-slate-200">
                  <th className="w-8 px-2 py-1.5"></th>
                  <th className="text-left px-2 py-1.5 text-[10px] font-semibold text-slate-400 uppercase">Riesgo</th>
                  <th className="text-left px-2 py-1.5 text-[10px] font-semibold text-slate-400 uppercase">Nivel</th>
                  <th className="text-left px-2 py-1.5 text-[10px] font-semibold text-slate-400 uppercase">Controles</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {availableCatalog.map(cr => (
                  <tr key={cr.id} className="hover:bg-[#a05e49]/5 cursor-pointer transition-colors" onClick={() => addFromCatalog(cr)}>
                    <td className="px-2 py-1.5 text-center"><Plus className="w-3.5 h-3.5 text-[#a05e49] mx-auto" /></td>
                    <td className="px-2 py-1.5 text-xs text-slate-800 font-medium">{cr.nombre}</td>
                    <td className="px-2 py-1.5"><span className={`text-[9px] font-bold px-1.5 py-0.5 rounded text-white ${RIESGO_NIVEL_COLORS[cr.nivel] || RIESGO_NIVEL_COLORS.MEDIO}`}>{cr.nivel || 'MEDIO'}</span></td>
                    <td className="px-2 py-1.5"><div className="flex flex-wrap gap-1">{cr.controles.map(c => <span key={c.id} className="text-[9px] bg-[#1B919B]/10 text-[#1B919B] px-1 py-0.5 rounded">{c.nombre}</span>)}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Manual entry with autocomplete */}
      <div className="p-4 bg-slate-50 rounded-lg space-y-3">
        <p className="text-[10px] font-semibold text-slate-400 uppercase">Agregar riesgo</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Riesgo with autocomplete */}
          <div className="relative" ref={riesgoRef}>
            <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Riesgo *</label>
            <input type="text" value={riesgoInput}
              onChange={e => { setRiesgoInput(e.target.value); setShowRiesgoSugg(true); }}
              onFocus={() => setShowRiesgoSugg(true)}
              placeholder="Escriba el riesgo..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="input-riesgo-desc" />
            {showRiesgoSugg && riesgoSuggestions.length > 0 && (
              <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-[160px] overflow-y-auto">
                {riesgoSuggestions.map(cr => (
                  <button key={cr.id} type="button"
                    onMouseDown={e => { e.preventDefault(); addFromCatalog(cr); setRiesgoInput(''); setShowRiesgoSugg(false); }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-[#a05e49]/5 border-b border-slate-50 last:border-0">
                    <span className="font-medium text-slate-800">{cr.nombre}</span>
                    {cr.controles.length > 0 && <span className="text-[9px] text-slate-400 ml-2">({cr.controles.length} controles)</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Control with autocomplete */}
          <div className="relative" ref={controlRef}>
            <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Control</label>
            <input type="text" value={controlInput}
              onChange={e => { setControlInput(e.target.value); setShowControlSugg(true); }}
              onFocus={() => setShowControlSugg(true)}
              placeholder="Escriba el control..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="input-riesgo-control" />
            {showControlSugg && controlSuggestions.length > 0 && (
              <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-[160px] overflow-y-auto">
                {controlSuggestions.map((c, i) => (
                  <button key={i} type="button"
                    onMouseDown={e => { e.preventDefault(); setControlInput(c.nombre); setControlTipo(c.tipo); setShowControlSugg(false); }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-[#1B919B]/5 border-b border-slate-50 last:border-0">
                    <span className="font-medium text-slate-700">{c.nombre}</span>
                    <span className="text-[9px] bg-[#1B919B]/10 text-[#1B919B] px-1 py-0.5 rounded ml-2">{c.tipo}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Tipo + Nivel + Agregar */}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Tipo Control</label>
              <select value={controlTipo} onChange={e => setControlTipo(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="select-control-tipo-manual">
                {CONTROL_TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Nivel</label>
              <select value={nivel} onChange={e => setNivel(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="select-riesgo-nivel">
                {['BAJO','MEDIO','ALTO','CRITICO'].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <button onClick={addManual} className="px-4 py-2 bg-[#a05e49] text-white text-sm rounded-lg hover:bg-[#8b4f3d] flex items-center gap-1 whitespace-nowrap" data-testid="add-riesgo-btn">
              <Plus className="w-4 h-4" />Agregar
            </button>
          </div>
        </div>
        <p className="text-[9px] text-slate-400">Los nuevos riesgos se agregan automaticamente al catalogo del area.</p>
      </div>

      {/* Assigned risks list */}
      {riesgos.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[10px] font-semibold text-slate-400 uppercase">{riesgos.length} riesgo(s) asignado(s) a esta actividad</p>
          {riesgos.map((r, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-slate-200" data-testid={`riesgo-item-${i}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded text-white flex-shrink-0 ${RIESGO_NIVEL_COLORS[r.nivel]}`}>{r.nivel}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{r.descripcion}</p>
                  {r.medida_control && <p className="text-xs text-slate-400 truncate">Control: {r.medida_control}</p>}
                </div>
              </div>
              <button onClick={() => removeRiesgo(i)} className="p-1 text-red-400 hover:text-red-600 flex-shrink-0 ml-2"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      ) : <p className="text-sm text-slate-400 text-center py-4">No hay riesgos asignados.</p>}

      {/* Other catalog risks (other areas) */}
      {otherCatalog.length > 0 && (
        <details className="group">
          <summary className="text-[10px] text-[#a05e49] font-medium cursor-pointer hover:underline">Ver riesgos de otras areas ({otherCatalog.length})</summary>
          <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[180px] overflow-y-auto mt-2">
            <table className="w-full">
              <tbody className="divide-y divide-slate-50">
                {otherCatalog.map(cr => (
                  <tr key={cr.id} className="hover:bg-[#a05e49]/5 cursor-pointer transition-colors" onClick={() => addFromCatalog(cr)}>
                    <td className="w-8 px-2 py-1.5 text-center"><Plus className="w-3.5 h-3.5 text-[#a05e49] mx-auto" /></td>
                    <td className="px-2 py-1.5 text-xs text-slate-800 font-medium">{cr.nombre}</td>
                    <td className="px-2 py-1.5"><span className={`text-[9px] font-bold px-1.5 py-0.5 rounded text-white ${RIESGO_NIVEL_COLORS[cr.nivel] || RIESGO_NIVEL_COLORS.MEDIO}`}>{cr.nivel}</span></td>
                    <td className="px-2 py-1.5 text-[9px] text-slate-400">{cr.controles.map(c => c.nombre).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}


export default function ActividadForm() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [opciones, setOpciones] = useState({});
  const [empresas, setEmpresas] = useState([]);

  const [form, setForm] = useState({
    titulo: '', descripcion: '', tipo_tarea: 'Programada', tipo_actividad: '',
    fecha_planificada: '', hora_inicio: '', fecha_termino: '', hora_termino: '', duracion_horas: 8,
    ubicacion: '', mandante: 'Compania Minera Lomas Bayas', mandante_ejecuta: false, empresa_id: null, empresas_ids: [], gerencia_mandante: '', area: '', area_id: '', responsable_area: '', sitio: '', sitio_id: '', nivel_riesgo: 'BAJO',
    personal: [],
    recursos: [],
    riesgos: [],
    preguntas_sso: DEFAULT_SSO.map(q => ({ pregunta: q, respuesta: null, observacion: '' })),
  });

  // Temp state for adding items
  const [newRecurso, setNewRecurso] = useState({ codigo: '', nombre: '', tipo: '' });
  const [areas, setAreas] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [tiposActividad, setTiposActividad] = useState([]);
  const [catalogoRecursos, setCatalogoRecursos] = useState([]);
  const [gerenciasMandante2, setGerenciasMandante2] = useState([]);
  const [catalogoRiesgos, setCatalogoRiesgos] = useState([]);

  useEffect(() => {
    axios.get(`${API}/actividades-operacionales/opciones`, { headers }).then(r => setOpciones(r.data)).catch(() => {});
    axios.get(`${API}/empresas-colaboradoras`, { headers }).then(r => setEmpresas(r.data)).catch(() => {});
    axios.get(`${API}/areas`, { headers }).then(r => setAreas(r.data)).catch(() => {});
    axios.get(`${API}/sectores`, { headers }).then(r => setSectores(r.data)).catch(() => {});
    axios.get(`${API}/tipos-actividad`, { headers }).then(r => setTiposActividad(r.data)).catch(() => {});
    axios.get(`${API}/catalogo-recursos`, { headers }).then(r => setCatalogoRecursos(r.data)).catch(() => {});
    axios.get(`${API}/gerencias-mandante2`, { headers }).then(r => setGerenciasMandante2(r.data)).catch(() => {});
    axios.get(`${API}/catalogo-riesgos`, { headers }).then(r => setCatalogoRiesgos(r.data)).catch(() => {});
  }, [token]);

  // Fetch responsables when area changes
  useEffect(() => {
    if (form.area_id) {
      axios.get(`${API}/areas/${form.area_id}/responsables`, { headers }).then(r => setResponsables(r.data)).catch(() => setResponsables([]));
    } else {
      setResponsables([]);
    }
  }, [form.area_id, token]);

  const u = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const calcDuration = (fi, hi, ft, ht) => {
    if (!fi || !hi || !ft || !ht) return null;
    const start = new Date(`${fi}T${hi}`);
    const end = new Date(`${ft}T${ht}`);
    const diffMs = end - start;
    if (diffMs <= 0) return null;
    return Math.round((diffMs / (1000 * 60 * 60)) * 10) / 10;
  };

  const uDate = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      // Auto-fill fecha_termino if empty
      if (field === 'fecha_planificada' && !next.fecha_termino) {
        next.fecha_termino = value;
      }
      const dur = calcDuration(next.fecha_planificada, next.hora_inicio, next.fecha_termino, next.hora_termino);
      if (dur !== null) next.duracion_horas = dur;
      return next;
    });
  };

  const removePersonal = (i) => u('personal', form.personal.filter((_, idx) => idx !== i));

  const addRecurso = () => {
    if (!newRecurso.codigo || !newRecurso.nombre) return;
    u('recursos', [...form.recursos, { ...newRecurso }]);
    setNewRecurso({ codigo: '', nombre: '', tipo: '' });
  };
  const removeRecurso = (i) => u('recursos', form.recursos.filter((_, idx) => idx !== i));

  const updateSSO = (i, field, value) => {
    const updated = [...form.preguntas_sso];
    updated[i] = { ...updated[i], [field]: value };
    u('preguntas_sso', updated);
  };

  const canNext = () => {
    if (step === 0) return form.titulo && form.fecha_planificada;
    return true;
  };

  const handleSave = async () => {
    if (!form.titulo || !form.fecha_planificada) { toast.error('Complete titulo y fecha'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/actividades-operacionales`, form, { headers });
      toast.success(`Actividad ${res.data.codigo} creada`);
      navigate(`/planificador/actividades/${res.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear'); }
    setSaving(false);
  };

  const sectionTitle = (icon, label) => {
    const Icon = icon;
    return <div className="flex items-center gap-2 mb-4"><Icon className="w-5 h-5 text-slate-500" /><h2 className="text-lg font-bold text-slate-800">{label}</h2></div>;
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4" data-testid="actividad-form">

      {/* Wizard Steps */}
      <div className="flex items-center justify-center gap-0 overflow-x-auto py-1">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = i === step;
          const completed = i < step;
          return (
            <React.Fragment key={s.key}>
              {i > 0 && <div className={`w-8 h-0.5 ${completed ? 'bg-emerald-400' : 'bg-slate-200'} shrink-0`} />}
              <button onClick={() => setStep(i)} className="flex flex-col items-center gap-1 px-2 shrink-0 cursor-pointer" data-testid={`step-${s.key}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                  active ? 'border-[#a05e49] bg-[#a05e49]/10 text-[#a05e49]' : completed ? 'border-[#a05e49] bg-[#a05e49] text-white' : 'border-slate-200 bg-white text-slate-400'
                }`}><Icon className="w-4.5 h-4.5" /></div>
                <span className={`text-[10px] font-medium whitespace-nowrap ${active ? 'text-[#a05e49]' : 'text-slate-400'}`}>{s.label}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        {/* Step 0: Datos Generales */}
        {step === 0 && (
          <div className="space-y-5">
            {sectionTitle(FileText, 'Datos Generales')}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Titulo de la Actividad *</label>
              <input type="text" value={form.titulo} onChange={e => u('titulo', e.target.value)} placeholder="Ej: Mantencion preventiva de equipos de izaje"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a05e49]/20 focus:border-[#c4836e]" data-testid="input-titulo" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Descripcion</label>
              <textarea value={form.descripcion} onChange={e => u('descripcion', e.target.value)} placeholder="Descripcion detallada de la actividad..." rows={3}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none resize-none" data-testid="input-descripcion" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Tipo de Tarea *</label>
                <select value={form.tipo_tarea} onChange={e => u('tipo_tarea', e.target.value)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" data-testid="select-tipo-tarea">
                  {(opciones.tipos_tarea || ['Programada', 'Emergente']).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Tipo de Actividad</label>
                <select value={form.tipo_actividad} onChange={e => u('tipo_actividad', e.target.value)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" data-testid="select-tipo-actividad">
                  <option value="">Seleccionar tipo</option>
                  {tiposActividad.length > 0
                    ? tiposActividad.map(t => <option key={t.id} value={t.nombre}>{t.nombre}</option>)
                    : (opciones.tipos_actividad || []).map(t => <option key={t} value={t}>{t}</option>)
                  }
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Fecha Inicio *</label>
                <input type="date" value={form.fecha_planificada} onChange={e => uDate('fecha_planificada', e.target.value)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" data-testid="input-fecha" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Hora Inicio</label>
                <input type="time" value={form.hora_inicio} onChange={e => uDate('hora_inicio', e.target.value)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" data-testid="input-hora-inicio" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Fecha Termino</label>
                <input type="date" value={form.fecha_termino} onChange={e => uDate('fecha_termino', e.target.value)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" data-testid="input-fecha-termino" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Hora Termino</label>
                <input type="time" value={form.hora_termino} onChange={e => uDate('hora_termino', e.target.value)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" data-testid="input-hora-termino" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Duracion (Horas)</label>
                <input type="number" value={form.duracion_horas} readOnly className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-600" data-testid="input-duracion" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Ubicacion</label>
              <input type="text" value={form.ubicacion} onChange={e => u('ubicacion', e.target.value)} placeholder="Ej: Planta de acido, Nivel 2"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a05e49]/20" data-testid="input-ubicacion" />
            </div>
          </div>
        )}

        {/* Step 1: Organizacion */}
        {step === 1 && (
          <div className="space-y-5">
            {sectionTitle(Building2, 'Organizacion')}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Mandante</label>
              <div className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-700 font-medium" data-testid="mandante-display">
                Compania Minera Lomas Bayas
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Gerencia donde se realiza la actividad *</label>
              <select value={form.gerencia_mandante} onChange={e => u('gerencia_mandante', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" data-testid="select-gerencia-mandante">
                <option value="">Seleccionar gerencia</option>
                {gerenciasMandante2.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-3 cursor-pointer py-1" data-testid="mandante-ejecuta">
              <input type="checkbox" checked={form.mandante_ejecuta} onChange={e => u('mandante_ejecuta', e.target.checked)}
                className="w-4.5 h-4.5 rounded border-slate-300 text-[#a05e49] focus:ring-emerald-500" />
              <span className="text-sm text-slate-700">La mandante ejecuta la actividad</span>
            </label>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Empresas Contratistas</label>
              <EmpresaSearchSelect
                empresas={empresas.filter(emp => !form.empresas_ids.includes(emp.id))}
                onSelect={(emp) => u('empresas_ids', [...form.empresas_ids, emp.id])}
              />
              {form.empresas_ids.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="empresas-chips">
                  {form.empresas_ids.map(eid => {
                    const emp = empresas.find(e => e.id === eid);
                    return (
                      <span key={eid} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#a05e49]/10 text-[#a05e49] text-xs font-medium rounded-full border border-[#a05e49]/30">
                        {emp?.razon_social || `ID:${eid}`}
                        <button onClick={() => u('empresas_ids', form.empresas_ids.filter(id => id !== eid))} className="hover:text-red-500 ml-0.5">&times;</button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Area</label>
                <select value={form.area_id} onChange={e => {
                  const areaId = e.target.value;
                  const areaObj = areas.find(a => a.id === areaId);
                  u('area_id', areaId);
                  u('area', areaObj?.nombre || '');
                  u('sitio', '');
                  u('sitio_id', '');
                  u('responsable_area', '');
                }}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" data-testid="select-area">
                  <option value="">Seleccionar area</option>
                  {areas.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Sector del Area</label>
                <select value={form.sitio} onChange={e => {
                  const sectorObj = sectores.find(s => s.nombre === e.target.value);
                  u('sitio', e.target.value);
                  u('sitio_id', sectorObj?.id || '');
                }}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" data-testid="select-sector">
                  <option value="">Seleccionar sector</option>
                  {(form.area_id ? sectores.filter(s => s.area_id === form.area_id) : sectores).map(s =>
                    <option key={s.id} value={s.nombre}>{s.nombre}{!form.area_id ? ` (${s.area_nombre})` : ''}</option>
                  )}
                </select>
                {!form.area_id && <p className="text-[10px] text-slate-400 mt-1">Seleccione un area para filtrar los sectores</p>}
              </div>
            </div>
            {form.area_id && (
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Responsable del Area</label>
                <select value={form.responsable_area} onChange={e => u('responsable_area', e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" data-testid="select-responsable">
                  <option value="">Seleccionar responsable</option>
                  {responsables.map(r => (
                    <option key={r.id} value={r.nombre}>{r.nombre}{r.cargo ? ` - ${r.cargo}` : ''}</option>
                  ))}
                </select>
                {responsables.length === 0 && <p className="text-[10px] text-slate-400 mt-1">No hay personas asignadas a esta area</p>}
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Nivel de Riesgo</label>
              <div className="flex gap-3" data-testid="riesgo-selector">
                {['BAJO', 'MEDIO', 'ALTO', 'CRITICO'].map(n => (
                  <button key={n} onClick={() => u('nivel_riesgo', n)}
                    className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${form.nivel_riesgo === n ? `${RIESGO_COLORS[n]} text-white` : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                    data-testid={`riesgo-${n.toLowerCase()}`}>{n}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Personal */}
        {step === 2 && (
          <PersonalStep
            token={token}
            mandanteEjecuta={form.mandante_ejecuta}
            empresasIds={form.empresas_ids}
            empresas={empresas}
            selected={form.personal}
            onAdd={(p) => { if (!form.personal.some(s => s.rut === p.rut)) u('personal', [...form.personal, p]); }}
            onRemove={(i) => u('personal', form.personal.filter((_, idx) => idx !== i))}
          />
        )}

        {/* Step 3: Recursos */}
        {step === 3 && (
          <RecursosStep
            catalogoRecursos={catalogoRecursos}
            selected={form.recursos}
            onAdd={(r) => { if (!form.recursos.some(fr => fr.codigo === r.codigo)) u('recursos', [...form.recursos, { codigo: r.codigo, nombre: r.nombre, tipo: r.tipo }]); }}
            onRemove={(i) => removeRecurso(i)}
          />
        )}

        {/* Step 4: Riesgos */}
        {step === 4 && (
          <RiesgosStep
            token={token}
            catalogoRiesgos={catalogoRiesgos}
            areaId={form.area_id}
            sectorId={form.sitio_id}
            areaNombre={form.area}
            sectorNombre={form.sitio}
            riesgos={form.riesgos}
            onUpdate={(riesgos) => u('riesgos', riesgos)}
          />
        )}

        {/* Step 5: Preguntas SSO */}
        {step === 5 && (
          <div className="space-y-5">
            {sectionTitle(Shield, 'Preguntas SSO (Seguridad y Salud Ocupacional)')}
            <p className="text-xs text-slate-400">Responda las preguntas de verificacion de seguridad antes de iniciar la actividad.</p>
            <div className="space-y-3">
              {form.preguntas_sso.map((q, i) => (
                <div key={i} className="p-4 rounded-lg border border-slate-200" data-testid={`sso-question-${i}`}>
                  <div className="flex items-start gap-3">
                    <span className="text-xs font-bold text-slate-400 mt-0.5">{i + 1}.</span>
                    <div className="flex-1">
                      <p className="text-sm text-slate-700 mb-2">{q.pregunta}</p>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input type="radio" name={`sso-${i}`} checked={q.respuesta === 'SI'} onChange={() => updateSSO(i, 'respuesta', 'SI')}
                            className="w-4 h-4 text-[#a05e49]" />
                          <span className="text-sm text-slate-600">Si</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input type="radio" name={`sso-${i}`} checked={q.respuesta === 'NO'} onChange={() => updateSSO(i, 'respuesta', 'NO')}
                            className="w-4 h-4 text-red-600" />
                          <span className="text-sm text-slate-600">No</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input type="radio" name={`sso-${i}`} checked={q.respuesta === 'NA'} onChange={() => updateSSO(i, 'respuesta', 'NA')}
                            className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-600">N/A</span>
                        </label>
                      </div>
                      {q.respuesta === 'NO' && (
                        <input type="text" value={q.observacion} onChange={e => updateSSO(i, 'observacion', e.target.value)}
                          placeholder="Observacion o justificacion..."
                          className="mt-2 w-full px-3 py-2 border border-red-200 bg-red-50/50 rounded-lg text-sm" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 6: Validacion */}
        {step === 6 && (
          <div className="space-y-5">
            {sectionTitle(CheckCircle, 'Resumen y Validacion')}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Personal', count: form.personal.length, icon: Users, color: form.personal.length > 0 ? 'emerald' : 'slate' },
                { label: 'Recursos', count: form.recursos.length, icon: Wrench, color: form.recursos.length > 0 ? 'emerald' : 'slate' },
                { label: 'Riesgos', count: form.riesgos.length, icon: AlertTriangle, color: form.riesgos.length > 0 ? 'emerald' : 'slate' },
                { label: 'Preguntas SSO', count: form.preguntas_sso.filter(q => q.respuesta).length + '/' + form.preguntas_sso.length, icon: Shield, color: form.preguntas_sso.every(q => q.respuesta) ? 'emerald' : 'amber' },
              ].map(card => (
                <div key={card.label} className="bg-slate-50 rounded-lg p-4 text-center">
                  <card.icon className={`w-5 h-5 mx-auto mb-1 ${card.color === 'emerald' ? 'text-emerald-500' : card.color === 'amber' ? 'text-amber-500' : 'text-slate-300'}`} />
                  <p className="text-xs font-semibold text-slate-500">{card.label}</p>
                  <p className={`text-lg font-bold ${card.color === 'emerald' ? 'text-[#a05e49]' : card.color === 'amber' ? 'text-amber-600' : 'text-slate-400'}`}>{card.count}</p>
                </div>
              ))}
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Datos de la Actividad</h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <div className="flex justify-between py-1"><span className="text-slate-400">Titulo</span><span className="text-slate-700 font-medium text-right">{form.titulo || '-'}</span></div>
                <div className="flex justify-between py-1"><span className="text-slate-400">Tipo Tarea</span><span className="text-slate-700">{form.tipo_tarea}</span></div>
                <div className="flex justify-between py-1"><span className="text-slate-400">Fecha Inicio</span><span className="text-slate-700">{form.fecha_planificada ? form.fecha_planificada.split('-').reverse().join('/') : '-'}</span></div>
                <div className="flex justify-between py-1"><span className="text-slate-400">Fecha Termino</span><span className="text-slate-700">{form.fecha_termino ? form.fecha_termino.split('-').reverse().join('/') : '-'}</span></div>
                <div className="flex justify-between py-1"><span className="text-slate-400">Horario</span><span className="text-slate-700">{form.hora_inicio || '-'} - {form.hora_termino || '-'} ({form.duracion_horas}h)</span></div>
                <div className="flex justify-between py-1"><span className="text-slate-400">Riesgo</span><span className={`text-xs font-bold px-2 py-0.5 rounded text-white ${RIESGO_COLORS[form.nivel_riesgo]}`}>{form.nivel_riesgo}</span></div>
                <div className="flex justify-between py-1"><span className="text-slate-400">Mandante</span><span className="text-slate-700">Compania Minera Lomas Bayas{form.mandante_ejecuta ? ' (ejecuta)' : ''}</span></div>
                <div className="flex justify-between py-1"><span className="text-slate-400">Ubicacion</span><span className="text-slate-700">{form.ubicacion || '-'}</span></div>
              </div>
            </div>
            {form.riesgos.some(r => r.nivel === 'CRITICO' || r.nivel === 'ALTO') && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Riesgos Altos/Criticos detectados</p>
                  <p className="text-xs text-amber-600 mt-0.5">{form.riesgos.filter(r => r.nivel === 'CRITICO' || r.nivel === 'ALTO').length} riesgo(s) requieren atencion del aprobador.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
          <button onClick={() => step > 0 ? setStep(step - 1) : navigate('/planificador/actividades')}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors" data-testid="btn-anterior">
            <ChevronLeft className="w-4 h-4" />Anterior
          </button>
          {step < 6 ? (
            <button onClick={() => setStep(step + 1)} disabled={!canNext()}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#a05e49] text-white text-sm font-medium rounded-lg hover:bg-[#8b4f3d] disabled:opacity-50 transition-colors" data-testid="btn-siguiente">
              Siguiente<ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSave} disabled={saving || !form.titulo || !form.fecha_planificada}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#a05e49] text-white text-sm font-medium rounded-lg hover:bg-[#8b4f3d] disabled:opacity-50 transition-colors" data-testid="btn-guardar">
              {saving ? 'Guardando...' : 'Crear Actividad'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
